import React, { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { noticeAria, noticeMotion } from '../../../../../shared/notice-rules';
/** Over video: always dark glass, in Dark and Light mode, for every tenant. */
export const IN_CALL_NOTICE_PALETTE = {
    bad: 'rgb(255, 138, 138)',
    badTint: 'rgba(255, 107, 107, 0.22)',
    blur: true,
    border: 'rgba(255, 255, 255, 0.14)',
    iconTint: 'rgba(255, 255, 255, 0.12)',
    muted: 'rgb(214, 216, 228)',
    ok: 'rgb(143, 209, 79)',
    okTint: 'rgba(143, 209, 79, 0.2)',
    shadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
    surface: 'rgba(14, 15, 28, 0.9)',
    text: 'rgb(244, 244, 247)'
};
const ICON_PATHS = {
    error: ['M12 9v4', 'M12 16h.01', 'M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0'],
    info: ['M12 8h.01', 'M11 12h1v4h1', 'M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0'],
    success: ['M5 12l5 5l10 -10'],
    close: ['M18 6l-12 12', 'M6 6l12 12']
};
function Glyph({ name }) {
    return (React.createElement("svg", { "aria-hidden": 'true', fill: 'none', focusable: 'false', height: 18, stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, viewBox: '0 0 24 24', width: 18 }, ICON_PATHS[name].map(path => React.createElement("path", { d: path, key: path }))));
}
function prefersReducedMotion() {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
const useStyles = makeStyles()((_theme, { edge, palette, reduced }) => {
    const motion = noticeMotion(reduced);
    // Slides in from its own edge: down from the top, up from the bottom.
    const from = edge === 'top' ? -motion.slidePx : motion.slidePx;
    const enter = `mn-notice-in-${edge}-${motion.slidePx}`;
    return {
        card: {
            alignItems: 'center',
            animation: `${enter} ${motion.enterMs}ms ease-out both`,
            background: palette.surface,
            border: `0.5px solid ${palette.border}`,
            borderRadius: 16,
            boxShadow: palette.shadow,
            boxSizing: 'border-box',
            color: palette.text,
            display: 'flex',
            fontSize: 14,
            gap: 10,
            lineHeight: 1.3,
            maxWidth: 420,
            minHeight: 52,
            padding: '4px 4px 4px 10px',
            pointerEvents: 'auto',
            width: '100%',
            ...palette.blur ? { WebkitBackdropFilter: 'blur(14px)', backdropFilter: 'blur(14px)' } : {},
            [`@keyframes ${enter}`]: {
                from: { opacity: 0, transform: `translateY(${from}px)` },
                to: { opacity: 1, transform: 'none' }
            }
        },
        leaving: {
            animation: `mn-notice-out ${motion.exitMs}ms ease-in both`,
            '@keyframes mn-notice-out': {
                from: { opacity: 1 },
                to: { opacity: 0 }
            }
        },
        plain: {
            paddingRight: 14
        },
        icon: {
            alignItems: 'center',
            background: palette.iconTint,
            borderRadius: '50%',
            color: palette.muted,
            display: 'flex',
            flex: 'none',
            height: 30,
            justifyContent: 'center',
            width: 30
        },
        iconError: {
            background: palette.badTint,
            color: palette.bad
        },
        iconSuccess: {
            background: palette.okTint,
            color: palette.ok
        },
        text: {
            flex: '1 1 auto',
            minWidth: 0,
            overflowWrap: 'break-word',
            padding: '6px 0'
        },
        action: {
            background: palette.action,
            border: 0,
            borderRadius: 12,
            color: palette.onAction,
            cursor: 'pointer',
            flex: 'none',
            font: 'inherit',
            fontWeight: 500,
            minHeight: 44,
            padding: '0 14px',
            whiteSpace: 'nowrap',
            '&:focus-visible': {
                outline: `2px solid ${palette.quietAction}`,
                outlineOffset: 2
            }
        },
        quiet: {
            background: 'transparent',
            color: palette.quietAction
        },
        close: {
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            borderRadius: 12,
            color: palette.muted,
            cursor: 'pointer',
            display: 'flex',
            flex: 'none',
            justifyContent: 'center',
            minHeight: 44,
            minWidth: 44,
            '&:focus-visible': {
                outline: `2px solid ${palette.quietAction}`,
                outlineOffset: 2
            }
        }
    };
});
/** One notice. Escape closes it while the focus is on it. */
const NoticeCard = ({ actionLabel, actionTestId, children, closeLabel, edge = 'bottom', id, leaving, onAction, onClose, palette, testId, text, variant }) => {
    const [reduced, setReduced] = useState(prefersReducedMotion);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return;
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReduced(query.matches);
        query.addEventListener?.('change', update);
        return () => query.removeEventListener?.('change', update);
    }, []);
    const { classes, cx } = useStyles({ edge, palette, reduced });
    const aria = noticeAria({ variant });
    const hasAction = Boolean(actionLabel && onAction);
    const showClose = !hasAction && Boolean(onClose);
    return (React.createElement("div", { "aria-atomic": 'true', "aria-live": aria.live, className: cx(classes.card, leaving && classes.leaving, !hasAction && !showClose && classes.plain), "data-notice-variant": variant, "data-testid": testId, id: id, onKeyDown: event => {
            if (event.key === 'Escape' && onClose) {
                event.stopPropagation();
                onClose();
            }
        }, role: aria.role },
        React.createElement("span", { className: cx(classes.icon, variant === 'error' && classes.iconError, variant === 'success' && classes.iconSuccess) },
            React.createElement(Glyph, { name: variant })),
        React.createElement("span", { className: classes.text },
            text,
            children),
        hasAction ? (React.createElement("button", { className: cx(classes.action, variant !== 'error' && classes.quiet), "data-testid": actionTestId, onClick: onAction, type: 'button' }, actionLabel)) : null,
        showClose ? (React.createElement("button", { "aria-label": closeLabel, className: classes.close, "data-testid": testId ? `${testId}-dismiss` : undefined, id: 'close-notification', onClick: onClose, type: 'button' },
            React.createElement(Glyph, { name: 'close' }))) : null));
};
export default NoticeCard;
